import { Link } from 'react-router-dom';
import {
  Mic,
  FileText,
  ShieldCheck,
  Layout,
  Download,
  Brain,
  Sparkles,
  Send,
  ArrowRight,
  Check,
} from 'lucide-react';
import Navbar from '../components/Landing/Navbar';
import Footer from '../components/Landing/Footer';
import { pricingPacks } from '../data/pricing';

const features = [
  {
    icon: Mic,
    title: 'Voice Dictation',
    description: 'Push-to-talk with real-time transcription. Speak naturally and watch your words appear instantly.',
  },
  {
    icon: FileText,
    title: 'AI Report Generation',
    description: 'Structured radiology reports generated from shorthand dictation with proper formatting.',
  },
  {
    icon: ShieldCheck,
    title: 'Smart Validation',
    description: 'Automatic checks for laterality, measurements, and report completeness before export.',
  },
  {
    icon: Layout,
    title: 'Template System',
    description: 'Customizable templates per modality and anatomy. Your reporting style, your way.',
  },
  {
    icon: Download,
    title: 'Export Formats',
    description: 'RTF, HL7, and plain text exports with one click. Seamless RIS/PACS integration.',
  },
  {
    icon: Brain,
    title: 'Classification Engine',
    description: 'Auto-categorize studies by modality and anatomy for consistent template selection.',
  },
];

const steps = [
  {
    icon: Mic,
    title: 'Dictate',
    description: 'Press and speak your findings naturally',
  },
  {
    icon: Sparkles,
    title: 'Generate',
    description: 'AI structures your report with proper formatting',
  },
  {
    icon: Send,
    title: 'Export',
    description: 'Send to RIS/PACS in RTF, HL7, or plain text',
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
                Your Voice. Your Report.{' '}
                <span className="text-teal-600">Perfected by AI.</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-lg">
                AI-powered radiology reporting that turns your voice dictation
                into structured, validated reports — ready for export to your
                RIS/PACS in seconds.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/login?register=true"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors"
                >
                  Start Free Trial — 20 Reports Free
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Login
                </Link>
              </div>
            </div>

            {/* Hero screenshot */}
            <div className="relative">
              <div className="rounded-xl shadow-2xl overflow-hidden border border-gray-200">
                <img
                  src="/screenshots/dashboard-hero.png"
                  alt="Radiology Dashboard — AI-generated report with dictation panel and editor"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-10 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-medium text-gray-400 mb-6">
            Trusted by radiologists across Australia
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            {['Clinic A', 'Clinic B', 'Clinic C', 'Clinic D'].map((name) => (
              <div
                key={name}
                className="h-8 w-24 bg-gray-200 rounded flex items-center justify-center"
              >
                <span className="text-xs text-gray-400 font-medium">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900">
              Everything you need for faster reporting
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
              From voice capture to validated export, every step of your
              radiology workflow is covered.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                  <f.icon className="text-teal-600" size={22} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <img
              src="/screenshots/dashboard-full.png"
              alt="Full dashboard view — dictation, AI report editor, and export toolbar"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900">How it works</h2>
            <p className="mt-3 text-gray-600">
              Three simple steps from dictation to export.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-teal-200" />

            {steps.map((step, i) => (
              <div key={step.title} className="text-center relative">
                <div className="w-24 h-24 bg-teal-50 border-2 border-teal-200 rounded-2xl flex items-center justify-center mx-auto mb-5 relative z-10">
                  <step.icon className="text-teal-600" size={36} />
                  <span className="absolute -top-3 -right-3 w-7 h-7 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900">
              Simple credit-based pricing
            </h2>
            <p className="mt-3 text-gray-600">
              Buy a report pack. Use at your own pace. All packs valid for 1
              year.
            </p>
          </div>

          {/* Free trial badge */}
          <div className="flex justify-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 text-teal-700 text-sm font-medium rounded-full">
              <Check size={16} />
              Start with 20 free reports — no credit card required
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPacks.map((pack) => (
              <div
                key={pack.name}
                className={`bg-white rounded-xl border p-6 relative ${
                  pack.popular
                    ? 'border-teal-400 shadow-lg ring-1 ring-teal-400'
                    : 'border-gray-200'
                }`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-teal-600 text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {pack.name}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {pack.reports.toLocaleString()} reports
                </p>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ${pack.price}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  ${pack.perReport} per report
                </p>
                <Link
                  to="/login?register=true"
                  className={`mt-6 block w-full text-center py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    pack.popular
                      ? 'bg-teal-600 hover:bg-teal-700 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Ready to transform your reporting workflow?
          </h2>
          <p className="mt-3 text-gray-600">
            Join radiologists across Australia who are saving hours every week.
          </p>
          <Link
            to="/login?register=true"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors"
          >
            Start Free Trial
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
